/* Self-contained module: only queries within its own root element (spec.md §3.0). */
import { StandardEvents } from "@shopify/events";

// A reload should start at the top (hero), not wherever the visitor last was.
// Horizon's scroll-container.js saves scrollTop into history.state on pagehide
// and restores it on pageshow — and a reload keeps history.state, so reloading
// dropped visitors mid-page (e.g. at Get In Touch). Back/forward restoration
// is left alone. Runs before pageshow, so the restore finds nothing to apply.
(function () {
  var nav = performance.getEntriesByType && performance.getEntriesByType("navigation")[0];
  if (!nav || nav.type !== "reload" || location.hash) {
    return;
  }
  history.scrollRestoration = "manual";
  if (history.state && typeof history.state === "object" && "scrollTop" in history.state) {
    var state = Object.assign({}, history.state);
    delete state.scrollTop;
    history.replaceState(state, "");
  }
  window.scrollTo(0, 0);
})();

(function () {
  var root = document.querySelector(".pr-nav");
  if (!root) {
    return;
  }

  var toggle = root.querySelector(".pr-nav__toggle");
  var links = root.querySelector(".pr-nav__links");

  if (toggle && links) {
    toggle.addEventListener("click", function () {
      links.classList.toggle("pr-nav__links--open");
    });
  }

  // Nav is position:fixed and overlays page content (pr-hero pulls itself up
  // behind it, other pages get #MainContent padding-top) — both rely on this
  // exact measured height rather than a guessed constant.
  function syncNavHeight() {
    document.documentElement.style.setProperty("--pr-nav-height", root.offsetHeight + "px");
  }
  syncNavHeight();
  window.addEventListener("resize", syncNavHeight);

  // Hide on scroll-down, reveal on scroll-up — always visible near the very
  // top so it doesn't vanish before the visitor has scrolled anywhere.
  //
  // Horizon's own base.css makes .page-wrapper (not window/body) the real
  // scroll container at desktop widths (>=990px): html/body get height:100dvh
  // + overflow:hidden there, so window's scrollY never changes and a listener
  // on window alone silently never fires. Below that width .page-wrapper has
  // no such override and body/window scroll normally instead. Reading
  // whichever one actually has scrollable overflow covers both — but "am I at
  // the top" is driven separately by an IntersectionObserver on a sentinel
  // element (below), which is correct regardless of which element is actually
  // scrolling, and doesn't depend on getScrollTop()'s heuristic at all.
  var pageWrapper = document.querySelector(".page-wrapper");

  function getScrollTop() {
    if (pageWrapper && pageWrapper.scrollHeight - pageWrapper.clientHeight > 1) {
      return pageWrapper.scrollTop;
    }
    return window.scrollY || document.documentElement.scrollTop || 0;
  }

  var atTop = true;
  var lastScrollTop = getScrollTop();
  var scrollTicking = false;

  function handleScroll() {
    var currentTop = getScrollTop();
    if (!atTop) {
      if (currentTop < lastScrollTop) {
        root.classList.remove("pr-nav--hidden");
      } else if (currentTop > lastScrollTop) {
        root.classList.add("pr-nav--hidden");
      }
    }
    lastScrollTop = currentTop;
    scrollTicking = false;
  }

  function onScroll() {
    if (!scrollTicking) {
      window.requestAnimationFrame(handleScroll);
      scrollTicking = true;
    }
  }

  window.addEventListener("scroll", onScroll, { passive: true });
  if (pageWrapper) {
    pageWrapper.addEventListener("scroll", onScroll, { passive: true });
  }

  var topSentinel = document.querySelector("[data-pr-nav-top-sentinel]");
  if (topSentinel && "IntersectionObserver" in window) {
    new IntersectionObserver(function (entries) {
      atTop = entries[entries.length - 1].isIntersecting;
      if (atTop) {
        root.classList.remove("pr-nav--hidden");
      }
    }).observe(topSentinel);
  } else {
    atTop = false;
  }

  // Active-section highlighting for same-page anchor links (e.g. "Products" ->
  // #product-carousel) — separate pages already get pr-nav__link--active from
  // Liquid's page_title comparison above.
  var sectionLinks = root.querySelectorAll("[data-pr-nav-section-link]");
  if (sectionLinks.length && "IntersectionObserver" in window) {
    var linksByTargetId = {};
    sectionLinks.forEach(function (link) {
      var hash = (link.href.split("#")[1] || "");
      var target = hash && document.getElementById(hash);
      if (target) {
        linksByTargetId[hash] = link;
      }
    });
    var targetIds = Object.keys(linksByTargetId);
    if (targetIds.length) {
      var sectionObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            var link = linksByTargetId[entry.target.id];
            if (link) {
              link.classList.toggle("pr-nav__link--active", entry.isIntersecting);
            }
          });
        },
        { rootMargin: "-40% 0px -40% 0px" }
      );
      targetIds.forEach(function (id) {
        sectionObserver.observe(document.getElementById(id));
      });
    }
  }
  // Badge counts bags, not cart lines — nested add-ons ride inside their bag
  // (same rule as snippets/pr-bag-count.liquid). Refreshed after every cart
  // change (adds from the showcase, quantity/remove inside the drawer), since
  // this custom header isn't one of Horizon's self-updating cart icons.
  var cartButton = root.querySelector(".pr-nav__cart");
  var badge = root.querySelector("[data-pr-cart-badge]");

  function bagCount(cart) {
    return cart.items.reduce(function (sum, item) {
      return item.parent_relationship ? sum : sum + item.quantity;
    }, 0);
  }

  function refreshBadge() {
    fetch("/cart.js", { headers: { Accept: "application/json" } })
      .then(function (res) {
        return res.json();
      })
      .then(function (cart) {
        var count = bagCount(cart);
        badge.textContent = count;
        badge.hidden = count === 0;
        cartButton.setAttribute("aria-label", "Cart, " + count + " items");
      })
      .catch(function (err) {
        console.error("pr-nav: cart badge refresh failed", err);
      });
  }

  // "Continue shopping" in the cart drawer points at the homepage showcase. On
  // the homepage itself a plain hash jump would scroll the page *behind* the
  // still-open drawer, so close the drawer first, then scroll once it's gone.
  // Elsewhere (e.g. Our Story) the normal navigation to /#product-carousel runs.
  document.addEventListener("click", function (event) {
    var link = event.target.closest("[data-pr-continue-shopping]");
    var drawer = link && link.closest("theme-drawer");
    var target = document.getElementById("product-carousel");
    if (!drawer || !target) {
      return;
    }
    event.preventDefault();
    drawer.close();
    setTimeout(function () {
      target.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 350);
  });

  document.addEventListener(StandardEvents.cartLinesUpdate, function (event) {
    if (event.promise) {
      event.promise.then(refreshBadge, function () {});
    } else {
      refreshBadge();
    }
  });
})();
